/**
 * Speech Intelligence UI utilities.
 * Pure functions for grouping, migration, and counting.
 */

/**
 * Group a flat Record<string, string> by value.
 * Returns sorted array of [targetName, triggerPhrases[]] tuples.
 *
 * Example:
 *   Input:  { "butter chicken": "New Delhi Murgh Makhani", "makhani": "New Delhi Murgh Makhani" }
 *   Output: [["New Delhi Murgh Makhani", ["butter chicken", "makhani"]]]
 */
export function groupByValue(data: Record<string, string>): [string, string[]][] {
  const groups: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!value) continue;
    if (!groups[value]) groups[value] = [];
    groups[value].push(key);
  }
  return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
}

/**
 * Split any "/" separated keys into individual entries.
 * Returns [normalizedData, wasModified].
 *
 * Example:
 *   Input:  { "butter chicken / murgh makhani": "New Delhi Murgh Makhani" }
 *   Output: [{ "butter chicken": "New Delhi Murgh Makhani", "murgh makhani": "New Delhi Murgh Makhani" }, true]
 */
export function normalizeSlashKeys(data: Record<string, string>): [Record<string, string>, boolean] {
  const result: Record<string, string> = {};
  let modified = false;

  for (const [key, value] of Object.entries(data)) {
    if (key.includes('/')) {
      modified = true;
      const parts = key.split('/').map(p => p.trim().toLowerCase()).filter(Boolean);
      for (const part of parts) {
        result[part] = value;
      }
    } else {
      result[key.toLowerCase().trim()] = value;
    }
  }

  return [result, modified];
}

/** Count total triggers (keys) in a Record. */
export function countTriggers(data: Record<string, string>): number {
  return Object.keys(data).length;
}

/** Count unique groups (unique values) in a Record. */
export function countGroups(data: Record<string, string>): number {
  return new Set(Object.values(data).filter(Boolean)).size;
}
